import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaRegEyeSlash, FaRegEye } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import logo from "../../assets/logo.png";
import meo from "../../assets/image/meo.jpg";
import axios from "axios";
import BASE_URL from "../../configs/api";
import { toast } from "react-toastify";
import { GoogleLogin } from "@react-oauth/google";
import { getProxyUrl } from "../../utils/proxy";
import ReCAPTCHA from "react-google-recaptcha";

const Login = ({
  isOpen,
  onClose,
  setUserInfo,
  setIsLoggedIn,
  onSwitchToRegister,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSwitchToRegister = () => {
    onSwitchToRegister();
  };

  const getUserInfo = async (accessToken) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/auth/profile/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = response.data;
      const userInfoData = {
        avatar: getProxyUrl(data.anh_dai_dien),
        avatar_gg: data.anh_dai_dien_url,
        username: data.username,
      };

      setUserInfo(userInfoData);
      localStorage.setItem("userInfo", JSON.stringify(userInfoData));
      setIsLoggedIn(true);
      localStorage.setItem("isLoggedIn", "true");
    } catch (err) {
      toast.error("Lỗi khi lấy thông tin người dùng.");
    }
  };

  const handleOverlayClick = () => onClose();
  const handleModalClick = (e) => e.stopPropagation();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    if (!recaptchaValue) {
      setError("Vui lòng xác minh reCAPTCHA.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BASE_URL}/api/auth/login/`, {
        username,
        password,
        recaptcha_token: recaptchaValue,
      });

      const data = response.data;
      if (data.access) {
        localStorage.setItem("accessToken", data.access);
        await getUserInfo(data.access);
        toast.success("Đăng nhập thành công!");
        navigate("/");
        onClose();
      } else {
        toast.error("Thông tin đăng nhập không hợp lệ.");
      }
    } catch (err) {
      const errorMessage = "Sai tài khoản hoặc mật khẩu";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    if (!recaptchaValue) {
      toast.error("Vui lòng xác minh reCAPTCHA trước khi đăng nhập Google.");
      return;
    }

    setIsLoading(true);
    try {
      const idToken = credentialResponse.credential;
      if (!idToken) {
        toast.error("Không nhận được thông tin từ Google.");
        return;
      }

      const res = await axios.post(`${BASE_URL}/api/auth/login/google/`, {
        id_token: idToken,
        recaptcha_token: recaptchaValue,
      });

      const data = res.data;
      if (data.access) {
        localStorage.setItem("accessToken", data.access);
        await getUserInfo(data.access);
        toast.success("Đăng nhập Google thành công!");
        onClose();
      } else {
        toast.error("Đăng nhập Google không thành công từ phía server.");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Vui lòng xác minh reCAPTCHA";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaChange = (value) => {
    setRecaptchaValue(value);
    if (error && error.includes("reCAPTCHA")) {
      setError(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[999] p-4"
      onClick={handleOverlayClick}
    >
      {/* --- THAY ĐỔI: Giảm max-w-4xl thành max-w-3xl để modal nhỏ gọn hơn --- */}
      <div
        className="bg-[#191B28] w-full max-w-3xl rounded-2xl shadow-2xl flex overflow-hidden relative animate-in fade-in duration-300"
        onClick={handleModalClick}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 text-gray-400 hover:text-white transition-colors duration-200 bg-black/20 hover:bg-black/40 rounded-full p-2"
          aria-label="Đóng"
        >
          <IoClose size={20} />
        </button>

        {/* --- THAY ĐỔI: Điều chỉnh tỷ lệ chiều rộng của panel ảnh --- */}
        <div className="hidden lg:block lg:w-[40%] relative bg-gradient-to-br from-gray-800 to-gray-900">
          <img
            src={meo}
            alt="Login background"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1e2545] via-transparent to-transparent"></div>
          {/* --- THAY ĐỔI: Giảm padding để nội dung gần nhau hơn --- */}
          <div className="relative z-10 flex flex-col items-start justify-end h-full p-6">
            <img src={logo} alt="Logo" className="w-[150px] h-auto mb-3" />
            <h3 className="text-white text-lg font-semibold mb-2">
              Chào mừng trở lại!
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              Đăng nhập để tiếp tục trải nghiệm.
            </p>
          </div>
        </div>

        {/* --- THAY ĐỔI: Điều chỉnh tỷ lệ chiều rộng và padding của form panel --- */}
        <div className="w-full lg:w-[60%] p-6 flex flex-col justify-center min-h-[550px]">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-6">
            <img src={logo} alt="Logo" className="w-[120px] h-auto" />
          </div>

          <div className="max-w-sm mx-auto w-full">
            {/* --- THAY ĐỔI: Giảm kích thước font chữ và margin bottom --- */}
            <h2 className="text-2xl font-bold text-white mb-1 text-center lg:text-left">
              Đăng nhập
            </h2>
            <p className="text-sm text-neutral-400 mb-6 text-center lg:text-left">
              Chưa có tài khoản?{" "}
              <button
                onClick={handleSwitchToRegister}
                className="text-yellow-500 hover:text-yellow-400 transition-colors duration-200 font-medium hover:underline"
              >
                Đăng ký ngay
              </button>
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-600/50 rounded-lg">
                <p className="text-red-400 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* --- THAY ĐỔI: Giảm khoảng cách giữa các trường trong form (space-y-4) --- */}
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-300 mb-2"
                >
                  Tên đăng nhập
                </label>
                <input
                  id="username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Nhập tên đăng nhập"
                  className="block w-full bg-[#2a314e] border border-white/20 rounded-lg py-2.5 px-4 text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all duration-200 hover:border-white/30"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-300 mb-2"
                >
                  Mật khẩu
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Nhập mật khẩu"
                    className="block w-full bg-[#2a314e] border border-white/20 rounded-lg py-2.5 px-4 pr-12 text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all duration-200 hover:border-white/30"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 px-4 flex items-center text-neutral-400 hover:text-white transition-colors duration-200"
                    aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <FaRegEyeSlash size={18} />
                    ) : (
                      <FaRegEye size={18} />
                    )}
                  </button>
                </div>
              </div>

              {/* reCAPTCHA */}
              <div className="flex justify-center pt-1">
                <div className="transform scale-90 origin-center">
                  <ReCAPTCHA
                    sitekey="6LdYZjcrAAAAAMRDdY0l10bwstM8v_yCazpJF2RA"
                    onChange={handleCaptchaChange}
                    theme="dark"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !recaptchaValue}
                className="w-full text-black font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background:
                    isLoading || !recaptchaValue
                      ? "linear-gradient(39deg, rgba(200, 200, 200, 1), rgba(220, 220, 220, 1))"
                      : "linear-gradient(39deg, rgba(254, 207, 89, 1), rgba(255, 241, 204, 1))",
                }}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                    Đang đăng nhập...
                  </div>
                ) : (
                  "Đăng nhập"
                )}
              </button>
            </form>

            {/* --- THAY ĐỔI: Giảm margin (my-6) --- */}
            <div className="relative my-6">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-[#1e2545] px-4 text-gray-400 font-medium">
                  HOẶC TIẾP TỤC VỚI
                </span>
              </div>
            </div>

            <div className="flex justify-center">
              <div
                className={`transition-opacity duration-200 ${
                  isLoading || !recaptchaValue
                    ? "opacity-50 pointer-events-none"
                    : ""
                }`}
              >
                <GoogleLogin
                  onSuccess={handleGoogleLogin}
                  onError={() => {
                    toast.error("Đăng nhập Google thất bại!");
                  }}
                  useOneTap={false}
                  theme="filled_blue"
                  shape="rectangular"
                  size="large"
                />
              </div>
            </div>

            {!recaptchaValue && !isLoading && (
              <p className="text-xs text-amber-400 text-center mt-3 animate-pulse">
                Vui lòng hoàn thành xác minh reCAPTCHA để tiếp tục
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
